import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Box,
  Calendar,
  CheckCircle2,
  DollarSign,
  Eye,
  EyeOff,
  FileText,
  Hash,
  ListOrdered,
  Route,
  ScanBarcode,
  Loader2,
  Mail as MailIcon,
  MapPin,
  Package,
  Plus,
  Printer,
  Trash2,
  Undo2,
} from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { classifyMail } from '@shipswift/shared';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api/client';
import AddressAutocomplete from '@/components/mail/AddressAutocomplete';
import { EnvelopePreview, ShippingLabelPreview } from '@/components/mail/LabelPreview';
import LabelDocumentViewer from '@/components/mail/LabelDocumentViewer';
import PDFViewerDialog from '@/components/mail/PDFViewerDialog';
import ScanFormsPanel from '@/components/mail/ScanFormsPanel';
import TrackingBadge from '@/components/mail/TrackingBadge';
import TrackingDialog from '@/components/mail/TrackingDialog';
import ReturnAddressDialog from '@/components/mail/ReturnAddressDialog';

/**
 * The recipient as the API wants it.
 *
 * Blank optional fields become null rather than "". Shippo treats a
 * present-but-empty attribute as a validation failure rather than as absent, so
 * an untouched phone box would otherwise fail every shipment that does not need
 * one — which is most of them.
 */
function toRecipientPayload(recipient) {
  return {
    ...recipient,
    address2: recipient.address2 || null,
    phone: recipient.phone?.trim() || null,
    email: recipient.email?.trim() || null,
  };
}

/** What USPS calls each shape, as the person choosing will recognise it. */
const MAIL_CLASS_LABELS = {
  LETTER: 'First-Class letter',
  FLAT: 'First-Class flat (large envelope)',
  PACKAGE: 'Package',
};

/** Services that carry content rules the shop has to stand behind. */
const RESTRICTED_SERVICES = ['Media Mail', 'Library Mail'];

/** USPS will not take back a First-Class letter or flat bought through EasyPost. */
function isUnrefundable(label) {
  return label.provider === 'easypost' && (label.mailClass === 'LETTER' || label.mailClass === 'FLAT');
}

const EMPTY_RECIPIENT = {
  name: '',
  phone: '',
  email: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

export default function MailPage() {
  const queryClient = useQueryClient();

  const [mediaId, setMediaId] = useState('');
  const [returnAddressId, setReturnAddressId] = useState('');
  const [recipient, setRecipient] = useState(EMPTY_RECIPIENT);
  const [trackingEmail, setTrackingEmail] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [dimensions, setDimensions] = useState({ length: '', width: '', height: '' });
  const [weight, setWeight] = useState({ lbs: '', oz: '' });
  const [insuranceValue, setInsuranceValue] = useState('');
  // Only used when EasyPost is the provider and the media is an envelope. USPS
  // decides letter / flat / package by thickness as much as by size.
  const [thickness, setThickness] = useState('0.25');
  const [mailClassChoice, setMailClassChoice] = useState('');
  const [contentConfirmed, setContentConfirmed] = useState(false);

  const [rates, setRates] = useState([]);
  const [carrier, setCarrier] = useState('');
  const [rateId, setRateId] = useState('');
  const [result, setResult] = useState(null);

  const [returnAddressDialogOpen, setReturnAddressDialogOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [trackingLabel, setTrackingLabel] = useState(null);
  const [pendingRefund, setPendingRefund] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  /**
   * Set when a delete was stopped because the carrier refused the refund.
   * Holds the reason, so the second dialog can say what the shop is writing off
   * rather than asking "are you sure?" about an unnamed cost.
   */
  const [deleteBlockedBy, setDeleteBlockedBy] = useState(null);
  const [showRefunded, setShowRefunded] = useState(false);

  // Stable for a given shipment, so a double-click or a resubmit is recognized
  // by the server as the same purchase rather than a second one. Regenerated
  // only when the form is reset for a new label.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const { data: media = [], isLoading: mediaLoading } = useQuery({
    queryKey: ['media'],
    queryFn: () => api.entities.Media.list(),
  });

  // Which API quotes new labels. Failing to load it is not worth blocking the
  // page for: Shippo is what the app did before there was a choice.
  const { data: providerInfo } = useQuery({
    queryKey: ['shippingProvider'],
    queryFn: () => api.request('/settings/shipping-provider'),
    staleTime: 30_000,
  });
  const usesEasyPost = providerInfo?.provider === 'easypost';
  // Offered whenever an EasyPost key exists, not only while it is the active
  // service: labels already bought through it still need to go on a form.
  const showScanForms = Boolean(providerInfo?.easypost?.configured);

  const { data: returnAddresses = [], isLoading: addressesLoading } = useQuery({
    queryKey: ['returnAddresses'],
    queryFn: () => api.entities.ReturnAddress.list(),
  });

  const { data: shippedLabels = [], isLoading: labelsLoading } = useQuery({
    queryKey: ['mailLabels'],
    // Refunded labels come back too. A refund no longer removes the record —
    // it is a money event worth being able to look up — so the queue has to be
    // able to show one, otherwise there would be no way to delete it later.
    queryFn: () => api.request('/labels?includeRefunded=true'),
  });

  const selectedMedia = media.find((m) => m.id === mediaId);
  const selectedReturnAddress = returnAddresses.find((a) => a.id === returnAddressId);
  const isEnvelope = selectedMedia?.type === 'ENVELOPE';

  useEffect(() => {
    if (!returnAddressId && returnAddresses.length > 0) {
      const preferred = returnAddresses.find((a) => a.isDefault) ?? returnAddresses[0];
      setReturnAddressId(preferred.id);
    }
  }, [returnAddresses, returnAddressId]);

  // Any change to what is being shipped invalidates the quote. Letting a stale
  // price stay on screen after the weight changed is how someone buys the wrong
  // service at the wrong price.
  useEffect(() => {
    setRates([]);
    setCarrier('');
    setRateId('');
  }, [mediaId, returnAddressId, recipient, dimensions, weight, insuranceValue, referenceNumber]);

  const totalWeightLbs = useMemo(() => {
    const lbs = Number.parseFloat(weight.lbs) || 0;
    const oz = Number.parseFloat(weight.oz) || 0;
    return lbs + oz / 16;
  }, [weight]);

  const carriers = useMemo(
    () => [...new Set(rates.map((rate) => rate.provider))],
    [rates],
  );
  const carrierRates = useMemo(
    () => rates.filter((rate) => rate.provider === carrier),
    [rates, carrier],
  );
  const selectedRate = rates.find((rate) => rate.rateId === rateId);
  const needsContentConfirmation = Boolean(
    selectedRate && RESTRICTED_SERVICES.includes(selectedRate.serviceName),
  );

  const buildParcel = () => {
    if (isEnvelope && selectedMedia) {
      // An envelope is quoted as a thin parcel: its own footprint, and a nominal
      // thickness the server fills in.
      return {
        weight: totalWeightLbs,
        length: selectedMedia.width,
        width: selectedMedia.height,
        height: usesEasyPost ? Number.parseFloat(thickness) || 0.25 : 0.25,
      };
    }
    return {
      weight: totalWeightLbs,
      length: Number.parseFloat(dimensions.length),
      width: Number.parseFloat(dimensions.width),
      height: Number.parseFloat(dimensions.height),
    };
  };

  /**
   * What USPS will treat this as. Worked out from the real numbers, so a 1 inch
   * thick envelope is not offered First-Class prices it cannot get.
   */
  const autoClass = (() => {
    if (!usesEasyPost || !isEnvelope || !selectedMedia) return 'PACKAGE';
    const parcel = buildParcel();
    if (!(parcel.weight > 0 && parcel.length > 0 && parcel.width > 0 && parcel.height > 0)) {
      return 'PACKAGE';
    }
    return classifyMail({
      lengthIn: parcel.length,
      widthIn: parcel.width,
      heightIn: parcel.height,
      weightOz: parcel.weight * 16,
    });
  })();
  const allowedClasses =
    autoClass === 'LETTER' ? ['LETTER', 'FLAT', 'PACKAGE'] : autoClass === 'FLAT' ? ['FLAT', 'PACKAGE'] : ['PACKAGE'];
  const mailClass = allowedClasses.includes(mailClassChoice) ? mailClassChoice : autoClass;

  // A different shape is a different set of prices, as much as a different weight.
  useEffect(() => {
    setRates([]);
    setCarrier('');
    setRateId('');
  }, [thickness, mailClass, usesEasyPost]);

  // The content confirmation belongs to one service; choosing another starts over.
  useEffect(() => {
    setContentConfirmed(false);
  }, [rateId]);

  const validate = () => {
    if (!selectedMedia) return 'Choose what to print on';
    if (!returnAddressId) return 'Choose a return address';
    if (!recipient.name.trim()) return 'Enter the recipient name';
    if (!recipient.address1.trim()) return 'Enter the street address';
    if (!recipient.city.trim()) return 'Enter the city';
    if (!/^[A-Za-z]{2}$/.test(recipient.state.trim())) return 'Enter a 2-letter state code';
    if (!/^\d{5}(-\d{4})?$/.test(recipient.zip.trim())) return 'Enter a 5-digit ZIP code';
    if (totalWeightLbs <= 0) return 'Enter a weight';
    if (usesEasyPost && isEnvelope && !(Number.parseFloat(thickness) > 0)) {
      return 'Enter the envelope thickness';
    }
    if (!isEnvelope) {
      const { length, width, height } = buildParcel();
      if (!(length > 0 && width > 0 && height > 0)) return 'Enter the package dimensions';
    }
    return null;
  };

  const ratesMutation = useMutation({
    mutationFn: () =>
      api.request('/rates', {
        method: 'POST',
        body: {
          recipient: toRecipientPayload(recipient),
          parcel: buildParcel(),
          returnAddressId,
          isEnvelope,
          mailClass,
          insuranceAmount: Number.parseFloat(insuranceValue) || undefined,
          referenceNumber: referenceNumber.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      setRates(data.rates);

      if (data.rates.length > 0) {
        const cheapest = data.rates[0];
        setCarrier(cheapest.provider);
        setRateId(cheapest.rateId);
      }

      data.warnings?.forEach((warning) => toast.warning(warning));
      toast.success(`${data.rates.length} rates found`);
    },
    onError: (error) => toast.error(error.message ?? 'Could not get rates'),
  });

  const purchaseMutation = useMutation({
    mutationFn: () =>
      api.request('/labels/single', {
        method: 'POST',
        body: {
          recipient: toRecipientPayload(recipient),
          parcel: buildParcel(),
          returnAddressId,
          rateId,
          // Which API quoted this rate, and its shipment: EasyPost needs both to buy.
          source: selectedRate?.source,
          shipmentId: selectedRate?.shipmentId,
          isEnvelope,
          mailClass,
          insuranceAmount: Number.parseFloat(insuranceValue) || undefined,
          referenceNumber: referenceNumber.trim() || undefined,
          trackingEmail: trackingEmail.trim() || undefined,
          idempotencyKey,
        },
      }),
    onSuccess: (label) => {
      setResult(label);
      queryClient.invalidateQueries({ queryKey: ['mailLabels'] });

      if (label.duplicate) {
        toast.info('This label was already bought — showing the existing one.');
      } else {
        toast.success(isEnvelope ? 'Postage created' : 'Label created');
      }

      if (label.emailStatus?.requested) {
        if (label.emailStatus.sent) {
          const count = label.emailStatus.recipients ?? 1;
          toast.success(
            count > 1 ? `Tracking emailed to ${count} addresses` : 'Tracking email sent',
          );
        } else {
          // The label is bought and printable either way; the customer just has
          // not been told. Worth saying out loud so someone can pass it on.
          toast.warning(
            `Label bought, but the tracking email did not go out: ${label.emailStatus.reason}`,
          );
        }
      }
      if (label.storedLocally === false) {
        // Printing still works — the app fetches the carrier's copy on demand.
        // This matters for a different reason: the shop's own archive of that
        // label is missing, and reprints will keep depending on the carrier's
        // link, which expires.
        toast.warning(
          'Bought and printable, but no copy was saved to this server. Tell whoever looks after the app.',
        );
      }
    },
    onError: (error) => toast.error(error.message ?? 'Could not create the label'),
  });

  const refundMutation = useMutation({
    mutationFn: (label) => api.request(`/labels/${label.id}/refund`, { method: 'POST' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mailLabels'] });

      if (data.refunded) {
        // Deliberately not "refunded" full stop: USPS reviews refunds in its
        // own time and the money is not back yet.
        toast.success(data.message ?? 'Refund requested');
      } else {
        toast.warning(`Marked refunded, but the carrier refused: ${data.error ?? data.status}`);
      }
    },
    onError: (error) => toast.error(error.message ?? 'Could not refund the label'),
    onSettled: () => setPendingRefund(null),
  });

  /**
   * Delete refunds first, and stops if the carrier says no.
   *
   * Deleting an unrefunded label throws away the tracking number and the
   * transaction id, which are the only way to chase the postage afterwards. So
   * a refusal turns into a second, explicit decision rather than being absorbed.
   */
  const deleteMutation = useMutation({
    mutationFn: ({ label, force }) =>
      api.request(`/labels/${label.id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mailLabels'] });
      setPendingDelete(null);
      setDeleteBlockedBy(null);

      if (data.forced) {
        toast.warning('Deleted. The postage was not refunded and the cost stands.');
      } else if (data.status === 'ALREADY_REFUNDED' || data.status === 'NOT_ATTEMPTED') {
        // Nothing was asked of the carrier just now, so do not imply it was.
        toast.success('Deleted');
      } else {
        toast.success('Refunded and deleted');
      }
    },
    onError: (error, variables) => {
      // 409 is the carrier refusing, which is a decision to put back to the
      // operator. Anything else is a genuine failure.
      if (error.status === 409 && !variables.force) {
        // Just the carrier's reason. The dialog supplies the rest, and showing
        // the server's full message too would say the same thing twice.
        setDeleteBlockedBy(error.details?.reason ?? error.message);
        return;
      }

      toast.error(error.message ?? 'Could not delete the label');
      setPendingDelete(null);
      setDeleteBlockedBy(null);
    },
  });

  const saveRecipientMutation = useMutation({
    mutationFn: () =>
      api.entities.DeliveryAddress.create({
        name: recipient.name,
        // Saved with the address so a customer who needed a phone number last
        // time does not need it typed again.
        phone: recipient.phone?.trim() || null,
        email: recipient.email?.trim() || null,
        address1: recipient.address1,
        address2: recipient.address2 || null,
        city: recipient.city,
        state: recipient.state,
        zip: recipient.zip,
        country: recipient.country,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      toast.success('Address saved');
    },
    onError: (error) => toast.error(error.message ?? 'Could not save the address'),
  });

  const handleGetRates = () => {
    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    ratesMutation.mutate();
  };

  const handleReset = () => {
    setRecipient(EMPTY_RECIPIENT);
    setTrackingEmail('');
    setReferenceNumber('');
    setDimensions({ length: '', width: '', height: '' });
    setWeight({ lbs: '', oz: '' });
    setInsuranceValue('');
    setRates([]);
    setCarrier('');
    setRateId('');
    setResult(null);
    setIdempotencyKey(crypto.randomUUID());
  };

  /**
   * What a confirmation dialog should show, including while it closes.
   *
   * Radix animates the dialog out, so it is still on screen for a moment after
   * the state that fed it has been cleared — long enough to flash "refund
   * $0.00" at whoever just pressed the button. Holding the last value means the
   * dialog fades out still saying what it said.
   */
  const lastRefundTarget = useRef(null);
  if (pendingRefund) lastRefundTarget.current = pendingRefund;
  const refundTarget = pendingRefund ?? lastRefundTarget.current;

  const lastDeleteTarget = useRef(null);
  if (pendingDelete) lastDeleteTarget.current = pendingDelete;
  const deleteTarget = pendingDelete ?? lastDeleteTarget.current;

  const activeLabels = shippedLabels.filter((label) => label.status === 'ACTIVE');
  const refundedLabels = shippedLabels.filter((label) => label.status === 'REFUNDED');
  const visibleLabels = showRefunded ? shippedLabels : activeLabels;
  const setupIncomplete = media.length === 0 || returnAddresses.length === 0;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Mail</h1>
        <p className="text-slate-600">Print postage on envelopes or single shipping labels</p>
      </div>

      <Tabs defaultValue="create">
        <TabsList className={`grid w-full ${showScanForms ? 'max-w-xl grid-cols-3' : 'max-w-md grid-cols-2'}`}>
          <TabsTrigger value="create" className="gap-2">
            <MailIcon className="w-4 h-4" />
            Create label
          </TabsTrigger>
          <TabsTrigger value="queue" className="gap-2">
            <ListOrdered className="w-4 h-4" />
            Shipped ({activeLabels.length})
          </TabsTrigger>
          {showScanForms && (
            <TabsTrigger value="scanforms" className="gap-2">
              <ScanBarcode className="w-4 h-4" />
              Scan forms
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="create" className="mt-6">
          {setupIncomplete && (
            <Alert className="border-yellow-200 bg-yellow-50 mb-6">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                {media.length === 0 && returnAddresses.length === 0
                  ? 'Add media and a return address in Settings before printing.'
                  : media.length === 0
                    ? 'Add a media type in Settings before printing.'
                    : 'Add a return address in Settings before printing.'}
              </AlertDescription>
            </Alert>
          )}

          {isEnvelope && (
            <Alert className="border-amber-200 bg-amber-50 mb-6">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Envelope mode:</strong> only USPS sells postage you can print on an
                envelope, so other carriers are hidden.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="flex items-center gap-2">
                  <MailIcon className="w-5 h-5" />
                  Shipment
                </CardTitle>
              </CardHeader>

              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    Print on
                  </Label>
                  {mediaLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  ) : (
                    <Select value={mediaId} onValueChange={setMediaId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select print media" />
                      </SelectTrigger>
                      <SelectContent>
                        {media.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name} ({item.width}&quot; × {item.height}&quot;) —{' '}
                            {item.type === 'ENVELOPE' ? 'Envelope' : 'Label'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Ship from
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={returnAddressId}
                      onValueChange={setReturnAddressId}
                      disabled={addressesLoading || returnAddresses.length === 0}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select return address" />
                      </SelectTrigger>
                      <SelectContent>
                        {returnAddresses.map((address) => (
                          <SelectItem key={address.id} value={address.id}>
                            {address.name}
                            {address.contactName ? ` — ${address.contactName}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setReturnAddressDialogOpen(true)}
                      aria-label="Add return address"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-green-600" />
                    Deliver to
                  </Label>
                  <AddressAutocomplete
                    formData={recipient}
                    setFormData={setRecipient}
                    onSave={() => saveRecipientMutation.mutate()}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tracking-email">Email tracking to (optional)</Label>
                  <Input
                    id="tracking-email"
                    type="email"
                    value={trackingEmail}
                    onChange={(e) => setTrackingEmail(e.target.value)}
                    placeholder="customer@example.com"
                  />
                </div>

                {!isEnvelope && selectedMedia && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Box className="w-4 h-4 text-indigo-600" />
                      Package dimensions (inches)
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {['length', 'width', 'height'].map((key) => (
                        <div key={key} className="space-y-1">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={dimensions[key]}
                            onChange={(e) =>
                              setDimensions({ ...dimensions, [key]: e.target.value })
                            }
                            placeholder="0"
                            className="text-right"
                            aria-label={key}
                          />
                          <p className="text-xs text-slate-500 text-center capitalize">{key}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Weight</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={weight.lbs}
                        onChange={(e) => setWeight({ ...weight, lbs: e.target.value })}
                        placeholder="0"
                        className="text-right"
                        aria-label="pounds"
                      />
                      <p className="text-xs text-slate-500 text-center">lbs</p>
                    </div>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="15.9"
                        value={weight.oz}
                        onChange={(e) => setWeight({ ...weight, oz: e.target.value })}
                        placeholder="0"
                        className="text-right"
                        aria-label="ounces"
                      />
                      <p className="text-xs text-slate-500 text-center">oz</p>
                    </div>
                  </div>
                </div>

                {usesEasyPost && isEnvelope && (
                  <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="space-y-2">
                      <Label htmlFor="thickness">Envelope thickness (inches)</Label>
                      <Input
                        id="thickness"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={thickness}
                        onChange={(e) => setThickness(e.target.value)}
                        className="bg-white"
                      />
                      <p className="text-xs text-slate-600">
                        USPS allows a letter up to 0.25 in thick and a flat up to 0.75 in.
                        Anything thicker is priced as a package.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Send as</Label>
                      <Select value={mailClass} onValueChange={setMailClassChoice}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {allowedClasses.map((value) => (
                            <SelectItem key={value} value={value}>
                              {MAIL_CLASS_LABELS[value]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {autoClass === 'PACKAGE' && totalWeightLbs > 0 && (
                        <p className="text-xs text-amber-700">
                          At this size and weight USPS prices it as a package, not First-Class
                          Mail. First-Class flats are limited to 13 oz and 0.75 in thick.
                        </p>
                      )}
                      {(mailClass === 'LETTER' || mailClass === 'FLAT') && (
                        <p className="text-xs text-amber-700">
                          First-Class letters and flats cannot be refunded once bought.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="insurance" className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    Insure for (optional)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      $
                    </span>
                    <Input
                      id="insurance"
                      type="number"
                      step="0.01"
                      min="0"
                      value={insuranceValue}
                      onChange={(e) => setInsuranceValue(e.target.value)}
                      placeholder="0.00"
                      className="pl-7"
                    />
                  </div>
                  {/*
                    Insurance is priced into the rate rather than added at
                    checkout, so without this the cover looks free — and
                    changing the amount silently reprices every rate on screen,
                    which is why they are cleared when it changes.
                  */}
                  {Number.parseFloat(insuranceValue) > 0 && (
                    <p className="text-xs text-amber-700">
                      {usesEasyPost
                        ? 'EasyPost charges for this on top of the postage shown below (about 1% of the value, $1 minimum).'
                        : 'The carrier charges for this. Rates shown below already include the cover.'}
                    </p>
                  )}
                </div>

                {/*
                  Here, with the other things that shape the shipment, rather
                  than over in the preview panel where it used to sit. It has to
                  be set before rates are fetched: Shippo attaches it to the
                  shipment, and the shipment is created by the rate call.
                */}
                <div className="space-y-2">
                  <Label htmlFor="reference" className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-indigo-600" />
                    Reference / RMA (optional)
                  </Label>
                  <Input
                    id="reference"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="e.g. RMA-12345"
                    maxLength={50}
                    disabled={Boolean(result)}
                  />
                  <p className="text-xs text-slate-500">
                    Printed on the label where the carrier supports it.
                  </p>
                  {referenceNumber.trim().length > 30 && (
                    <p className="text-xs text-amber-700">
                      USPS prints the first 30 characters and UPS the first 35. Longer references
                      are cut short on the label.
                    </p>
                  )}
                </div>

                {!result && (
                  <>
                    <Button
                      onClick={handleGetRates}
                      disabled={ratesMutation.isPending || setupIncomplete}
                      className="w-full bg-green-600 hover:bg-green-700 h-11"
                    >
                      {ratesMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Getting rates…
                        </>
                      ) : rates.length > 0 ? (
                        'Refresh rates'
                      ) : (
                        'Get shipping rates'
                      )}
                    </Button>

                    {rates.length > 0 && (
                      <>
                        <p className="text-xs text-slate-500">
                          Rates from {rates[0]?.source === 'easypost' ? 'EasyPost' : 'Shippo'}
                          {rates[0]?.source === 'easypost'
                            ? '. Media Mail and Library Mail appear only if your EasyPost account offers them.'
                            : ''}
                        </p>

                        <div className="space-y-2">
                          <Label>Carrier</Label>
                          <Select
                            value={carrier}
                            onValueChange={(next) => {
                              setCarrier(next);
                              const first = rates.find((rate) => rate.provider === next);
                              setRateId(first?.rateId ?? '');
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                            <SelectContent>
                              {carriers.map((name) => (
                                <SelectItem key={name} value={name}>
                                  {name.toUpperCase()}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Service</Label>
                          <Select value={rateId} onValueChange={setRateId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {carrierRates.map((rate) => (
                                <SelectItem key={rate.rateId} value={rate.rateId}>
                                  {rate.serviceName} — ${rate.amount.toFixed(2)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {selectedRate && (
                            <div className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div>
                                <p className="text-sm font-semibold text-green-900">
                                  {selectedRate.serviceName}
                                </p>
                                {selectedRate.estimatedDays != null && (
                                  <p className="text-xs text-green-700">
                                    Est. {selectedRate.estimatedDays} day
                                    {selectedRate.estimatedDays === 1 ? '' : 's'}
                                  </p>
                                )}
                              </div>
                              <p className="text-lg font-bold text-green-700">
                                ${selectedRate.amount.toFixed(2)}
                              </p>
                            </div>
                          )}
                        </div>

                        {needsContentConfirmation && (
                          <Alert className="border-amber-200 bg-amber-50">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800 space-y-2">
                              <p>
                                <strong>{selectedRate.serviceName}</strong> is only for qualifying
                                material: books of at least 8 pages, sound and video recordings,
                                printed music and educational charts. It cannot carry advertising,
                                and USPS may open the package to check.
                              </p>
                              <label className="flex items-start gap-2 cursor-pointer">
                                <Checkbox
                                  checked={contentConfirmed}
                                  onCheckedChange={(checked) => setContentConfirmed(checked === true)}
                                  className="mt-0.5"
                                />
                                <span className="text-sm">
                                  This package contains only qualifying material.
                                </span>
                              </label>
                            </AlertDescription>
                          </Alert>
                        )}

                        <Button
                          onClick={() => purchaseMutation.mutate()}
                          disabled={
                            purchaseMutation.isPending ||
                            !rateId ||
                            (needsContentConfirmation && !contentConfirmed)
                          }
                          className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg shadow-lg"
                        >
                          {purchaseMutation.isPending ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Buying {isEnvelope ? 'postage' : 'label'}…
                            </>
                          ) : (
                            <>
                              <Printer className="w-5 h-5 mr-2" />
                              Buy {isEnvelope ? 'postage' : 'label'}
                              {selectedRate ? ` — $${selectedRate.amount.toFixed(2)}` : ''}
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </>
                )}

                {result && (
                  <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-900 font-semibold">
                      <CheckCircle2 className="w-5 h-5" />
                      {isEnvelope ? 'Postage created' : 'Label created'}
                    </div>

                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-xs text-green-700">Tracking number</p>
                        <p className="font-mono font-semibold text-green-900">
                          {result.trackingNumber ?? 'Not provided by carrier'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-green-700">Cost</p>
                        <p className="text-lg font-bold text-green-900">
                          ${Number(result.cost).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/*
                      No Print button here any more: the label itself is on the
                      right, with its own Print, Download and Open. Two Print
                      buttons a few inches apart, doing subtly different things,
                      is worse than one in the obvious place.
                    */}
                    <div className="pt-2">
                      <Button onClick={handleReset} variant="outline" className="w-full">
                        New {isEnvelope ? 'postage' : 'label'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg h-fit">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardTitle>
                  {result ? (isEnvelope ? 'Postage' : 'Shipping label') : 'Preview'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/*
                  Once the label exists, this panel stops guessing at it and
                  shows the real thing. The mock preview is for deciding whether
                  to spend the money; afterwards the only useful thing here is
                  the label itself, ready to print. Walking to the Shipped tab
                  to print something bought ten seconds ago is a trip nobody
                  should have to make.

                  The reference field goes with it — it is an input to a
                  purchase that has already happened, and was only disabled.
                */}
                {result ? (
                  <LabelDocumentViewer
                    documentUrl={`/api/documents/mail-labels/${result.id}/label`}
                    title={isEnvelope ? 'Postage' : 'Shipping label'}
                    mailLabelIds={[result.id]}
                    frameClassName="min-h-[28rem]"
                  />
                ) : (
                  <>
                  {!selectedMedia ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center bg-slate-50">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">Choose media to see a preview</p>
                    </div>
                  ) : isEnvelope ? (
                    <EnvelopePreview
                      returnAddress={selectedReturnAddress}
                      recipient={recipient}
                      widthInches={selectedMedia.width}
                      heightInches={selectedMedia.height}
                    />
                  ) : (
                    <ShippingLabelPreview
                      returnAddress={selectedReturnAddress}
                      recipient={recipient}
                      carrier={carrier || 'USPS'}
                      serviceName={selectedRate?.serviceName ?? ''}
                      weightLbs={totalWeightLbs}
                      referenceNumber={referenceNumber}
                      widthInches={selectedMedia.width}
                      heightInches={selectedMedia.height}
                    />
                  )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="queue" className="mt-6">
          <Card className="border-none shadow-lg">
            <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  <ListOrdered className="w-5 h-5" />
                  Shipped
                </CardTitle>

                {refundedLabels.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowRefunded((shown) => !shown)}
                  >
                    {showRefunded ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showRefunded ? 'Hide' : 'Show'} refunded ({refundedLabels.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {labelsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : visibleLabels.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-1">No labels yet</h3>
                  <p className="text-slate-500">Labels you buy appear here.</p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {visibleLabels.map((label) => (
                    <li key={label.id}>
                      <Card
                        className={
                          label.status === 'REFUNDED'
                            ? 'border-slate-200 bg-slate-50'
                            : 'border-slate-200'
                        }
                      >
                        <CardContent className="p-4">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <div
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                                    label.status === 'REFUNDED'
                                      ? 'bg-slate-400'
                                      : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                                  }`}
                                >
                                  <Package className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                  <h4 className="font-semibold text-slate-900 truncate flex items-center gap-2">
                                    {label.recipientName}
                                    {label.status === 'REFUNDED' && (
                                      <Badge
                                        variant={label.refundError ? 'destructive' : 'secondary'}
                                        className="shrink-0 font-normal"
                                        // The carrier's own words, for whoever
                                        // has to chase it.
                                        title={label.refundError ?? undefined}
                                      >
                                        {label.refundError ? 'Refund refused' : 'Refunded'}
                                      </Badge>
                                    )}
                                    {label.status !== 'REFUNDED' && (
                                      <TrackingBadge status={label.trackingStatus} />
                                    )}
                                  </h4>
                                  <p className="text-sm text-slate-600">
                                    {label.carrier.toUpperCase()} — {label.serviceName}
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-1 text-sm text-slate-600">
                                <div className="flex items-center gap-2">
                                  <Hash className="w-4 h-4 shrink-0" />
                                  {label.trackingUrl ? (
                                    <a
                                      href={label.trackingUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-mono text-blue-600 hover:underline"
                                    >
                                      {label.trackingNumber}
                                    </a>
                                  ) : (
                                    <span className="font-mono">{label.trackingNumber ?? '—'}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 shrink-0" />
                                  <span className="truncate">
                                    {label.recipientAddress1}, {label.recipientCity},{' '}
                                    {label.recipientState} {label.recipientZip}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 shrink-0" />
                                  <span>
                                    {formatInTimeZone(
                                      new Date(label.createdAt),
                                      'America/New_York',
                                      'MMM d, yyyy h:mm a',
                                    )}
                                  </span>
                                </div>
                                {/*
                                  Struck through only when the money is actually
                                  coming back. A refused refund still cost what
                                  it cost.
                                */}
                                <div
                                  className={`flex items-center gap-2 font-semibold ${
                                    label.status === 'REFUNDED' && !label.refundError
                                      ? 'text-slate-400'
                                      : 'text-green-700'
                                  }`}
                                >
                                  <DollarSign className="w-4 h-4 shrink-0" />
                                  <span
                                    className={
                                      label.status === 'REFUNDED' && !label.refundError
                                        ? 'line-through'
                                        : ''
                                    }
                                  >
                                    ${Number(label.cost).toFixed(2)}
                                  </span>
                                  {/*
                                    Shown because there is otherwise no way to
                                    tell an insured label from an uninsured one
                                    after the fact — the cover is part of the
                                    price, not a separate line.
                                  */}
                                  {Number(label.insuranceAmount) > 0 && (
                                    <span className="font-normal text-slate-500">
                                      · insured ${Number(label.insuranceAmount).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2">
                              {/*
                                A refunded label is void. Reprinting one would
                                put postage on a parcel the carrier has already
                                been asked to take back.
                              */}
                              {label.status === 'ACTIVE' && (
                                <>
                                  {label.trackingNumber && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-2"
                                      onClick={() => setTrackingLabel(label)}
                                    >
                                      <Route className="w-4 h-4" />
                                      Track
                                    </Button>
                                  )}

                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    onClick={() =>
                                      setViewing({
                                        url: `/api/documents/mail-labels/${label.id}/label`,
                                        title: `Label — ${label.recipientName}`,
                                        mailLabelIds: [label.id],
                                      })
                                    }
                                  >
                                    <Printer className="w-4 h-4" />
                                    Print
                                  </Button>

                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    onClick={() => setPendingRefund(label)}
                                    disabled={refundMutation.isPending || isUnrefundable(label)}
                                    title={
                                      isUnrefundable(label)
                                        ? 'USPS does not refund First-Class letters and flats'
                                        : undefined
                                    }
                                  >
                                    <Undo2 className="w-4 h-4" />
                                    Refund
                                  </Button>
                                </>
                              )}

                              <Button
                                variant="destructive"
                                size="sm"
                                className="gap-2"
                                onClick={() => {
                                  setDeleteBlockedBy(null);
                                  setPendingDelete(label);
                                }}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {showScanForms && (
          <TabsContent value="scanforms" className="mt-6">
            <ScanFormsPanel />
          </TabsContent>
        )}
      </Tabs>

      <AlertDialog
        open={Boolean(pendingRefund)}
        onOpenChange={(open) => !open && setPendingRefund(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refund this label?</AlertDialogTitle>
            <AlertDialogDescription>
              This asks {refundTarget?.carrier?.toUpperCase()} to refund $
              {Number(refundTarget?.cost ?? 0).toFixed(2)}. Carriers usually take a few days to
              process a refund, and will not refund a label that has already been scanned.
              <br />
              <br />
              The label stays in the list, marked refunded, so you can still see what it cost and
              chase it if the money does not come back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => refundMutation.mutate(pendingRefund)}
              className="bg-red-600 hover:bg-red-700"
            >
              Refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/*
        Delete refunds first. The second state — after the carrier has refused —
        is a different question with a different answer, so it gets its own
        wording rather than the same dialog with a red button.
      */}
      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setDeleteBlockedBy(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteBlockedBy ? 'The carrier would not refund this' : 'Delete this label?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteBlockedBy ? (
                <>
                  {deleteBlockedBy}
                  <br />
                  <br />
                  Deleting it now means the ${Number(deleteTarget?.cost ?? 0).toFixed(2)} is gone
                  and there will be no record left to chase it with.
                </>
              ) : deleteTarget?.status === 'REFUNDED' ? (
                <>
                  This label has already been refunded. Deleting removes it and its stored copy for
                  good — the entry in Activity is all that will remain.
                </>
              ) : (
                <>
                  This asks {deleteTarget?.carrier?.toUpperCase()} to refund $
                  {Number(deleteTarget?.cost ?? 0).toFixed(2)} first, then removes the label and
                  its stored copy for good.
                  <br />
                  <br />
                  If the carrier refuses the refund, nothing is deleted and you will be asked again.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Kept open on the first refusal so the second question can be
                // asked in place; Radix would otherwise close on any action.
                event.preventDefault();
                deleteMutation.mutate({
                  label: pendingDelete,
                  force: Boolean(deleteBlockedBy),
                });
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {deleteBlockedBy
                ? 'Delete anyway and write off the cost'
                : deleteTarget?.status === 'REFUNDED'
                  ? 'Delete'
                  : 'Refund and delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReturnAddressDialog
        open={returnAddressDialogOpen}
        onClose={() => setReturnAddressDialogOpen(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['returnAddresses'] })}
      />

      {trackingLabel && (
        <TrackingDialog label={trackingLabel} onClose={() => setTrackingLabel(null)} />
      )}

      {viewing && (
        <PDFViewerDialog
          open
          onClose={() => setViewing(null)}
          documentUrl={viewing.url}
          title={viewing.title}
          mailLabelIds={viewing.mailLabelIds ?? []}
        />
      )}
    </div>
  );
}
